// src/api/borrowerPortal/shortlistedModal.js
import { apiPost, apiGet } from "../client";
import { getBorrowerEmail } from "../../utils/auth";
import { listApplications, createApplication } from "./applications";
import {
  formatInvoiceFinanceProductTitle,
  resolveInvoiceFinanceFacilityType,
} from "../invoiceFinanceSourcing";

const firstNonEmpty = (...values) => values.find((value) => String(value ?? "").trim() !== "");

const parseMoney = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const num = Number.parseFloat(String(value).replace(/[£,\s]/g, ""));
  return Number.isFinite(num) ? num : fallback;
};

function readSourcingSnapshot() {
  try {
    const raw = localStorage.getItem("ds_sourcing_last_search");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function resolveProductTermFields(source) {
  const rawValue = firstNonEmpty(
    source?.product_term,
    source?.productTerm,
    source?.product_term_years,
    source?.product_termsyears,
    source?._raw?.product_term,
    source?._raw?.product_term_years,
    source?._raw?.product_termsyears
  );
  if (rawValue == null) {
    return { productTermRaw: null, productTermNumeric: null };
  }

  const rawText = String(rawValue).trim();
  if (!rawText) {
    return { productTermRaw: null, productTermNumeric: null };
  }

  if (rawText.toLowerCase() === "lifetime") {
    return { productTermRaw: "Lifetime", productTermNumeric: null };
  }

  const parsed = Number.parseFloat(rawText);
  if (Number.isFinite(parsed)) {
    return { productTermRaw: String(parsed), productTermNumeric: parsed };
  }

  return { productTermRaw: rawText, productTermNumeric: null };
}

function toBackendProductTermsYearsValue(productTermRaw, productTermNumeric) {
  const allowed = new Set(["0.5", "1", "2", "3", "5", "10", "Lifetime"]);
  if (productTermRaw == null || String(productTermRaw).trim() === "") return "";
  if (String(productTermRaw).trim().toLowerCase() === "lifetime") return "Lifetime";
  if (productTermNumeric == null || !Number.isFinite(productTermNumeric)) return "";
  const key = String(productTermNumeric);
  return allowed.has(key) ? key : "";
}

/* ---------------- API Functions ---------------- */

/**
 * Add selected product to applicant record (Cart Apply API)
 * Ensures an Application doc exists before linking.
 * 
 * @param {Object} productData - Product information
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const addToApplicantRecord = async (productData) => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");

    // 1. Find or Create Application
    let applicationId;

    // Check for existing application for this user
    const existingApps = await listApplications({ applicant: userEmail });

    if (existingApps && existingApps.length > 0) {
      // Use the most recent one (or first one)
      applicationId = existingApps[0].name;
    } else {
      // Extract loan purpose from product data if available, otherwise default to "Purchase"
      const loanPurpose = productData.loan_purpose ||
        productData.loanPurpose ||
        productData.loan_purpose_type ||
        productData.purpose ||
        "Purchase";

      // Create new application
      const newApp = await createApplication({
        applicant: userEmail,
        status: "Pre Enquiry",
        product: productData.id,
        lender: productData.lenderName,
        loan_purpose: loanPurpose, // Use loan purpose from product, or default to "Purchase"
      });
      applicationId = newApp.name;
    }

    if (!applicationId) throw new Error("Could not resolve Application ID");

    // 2. Prepare payload for add_to_applicant_record
    const payload = {
      user: userEmail,            // Required by backend
      application_id: applicationId, // Required by backend

      product_id: productData.id,
      // Applications DocType uses product_type as category (BTL/IF); Shortlisted Product does not.
      product_type: productData.product_category || productData.category || productData.productType,
      category: productData.product_category || productData.category || null,
      product_category: productData.product_category || productData.category || null,
      lender_id: productData.lenderId,
      lender_name: productData.lenderName,
      product_name: productData.name,
      rate: productData.rate,
      rate_type: productData.rateType,
      term_months: productData.termMonths,
      ltv: productData.ltv,
      monthly_cost: productData.monthlyCost,
      total_cost: productData.totalCost,
      fees: productData.fees,

      // Store shortlist inputs on the Application doc so admin can render EOR
      // without needing borrower-authenticated shortlist fetch.
      deposit_amount:
        productData.deposit_amount ??
        productData.depositAmount ??
        0.0,
      est_rent:
        productData.est_rent ??
        productData.estimated_rent ??
        productData.estimatedRent ??
        productData.estRent ??
        0.0,
      estimated_rent:
        productData.estimated_rent ??
        productData.est_rent ??
        productData.estimatedRent ??
        productData.estRent ??
        0.0,

      // Invoice Finance Application fields
      sector: productData.sector ?? null,
      projected_turnover: productData.projected_turnover ?? productData.projectedTurnover ?? 0.0,
      facility_required: productData.facility_required ?? productData.facilityRequired ?? 0.0,
      facility_type: (() => {
        const cat = String(productData.product_category || productData.category || productData.productType || "").toUpperCase();
        if (cat === "IF") return resolveInvoiceFinanceFacilityType(productData);
        return productData.facility_type ?? productData.facilityType ?? null;
      })(),
      net_advance: productData.net_advance ?? productData.netAdvance ?? 0.0,
      additional_borrowing_amount:
        productData.additional_borrowing_amount ?? productData.additionalBorrowingAmount ?? 0.0,
      additional_borrowing: productData.additional_borrowing ?? 0,
      confidential_facility: productData.confidential_facility ?? productData.confidentialFacility ?? 0,
      confidential: productData.confidential ?? 0,
      disclosure: productData.disclosure ?? 0,
      ip_percent: productData.ip_percent ?? productData.invoicePurchasePct ?? 0.0,
      discount_rate_percent:
        productData.discount_rate_percent ?? productData.discountRatePercent ?? 0.0,
      simple_service_fee: productData.simple_service_fee ?? productData.annualCostPct ?? 0.0,
      min_service_fee: productData.min_service_fee ?? productData.minServiceFee ?? 0.0,
      apr: productData.apr ?? productData.ratePct ?? productData.rate ?? 0.0,
      arrangement_fee: productData.arrangement_fee ?? productData.arrangementFee ?? 0.0,
      interest_rate: productData.interest_rate ?? productData.rate ?? null,
      monthly_repayments:
        productData.monthly_repayments ?? productData.monthly_payment ?? productData.monthlyCost ?? 0,
      total_repayable: productData.total_repayable ?? 0.0,
      first_12_month_interest: productData.first_12_month_interest ?? 0.0,
      contract_term_months:
        productData.contract_term_months ?? productData.term_months ?? productData.termMonths ?? 0,
      ...(() => {
        const cat = String(
          productData.product_category || productData.category || productData.productType || ""
        ).toUpperCase();
        if (cat !== "IF") return {};
        return {
          ab_arrangement_fee: parseMoney(
            productData.ab_arrangement_fee ?? productData.abCalc?.arrangementFee,
            0
          ),
          ab_cost_over_product_term: parseMoney(
            productData.ab_cost_over_product_term ??
              productData.ab_cost_over_term ??
              productData.abCalc?.costOverTerm,
            0
          ),
        };
      })(),
    };

    if (process.env.NODE_ENV === "development") {
      console.log("📌 [addToApplicantRecord] payload deposit/rent:", {
        deposit_amount: payload.deposit_amount,
        est_rent: payload.est_rent,
        estimated_rent: payload.estimated_rent,
        source_deposit_amount: productData.deposit_amount ?? productData.depositAmount ?? undefined,
        source_est_rent:
          productData.est_rent ?? productData.estimated_rent ?? productData.estimatedRent ?? productData.estRent ?? undefined,
      });
    }

    // 3. Call the backend method
    const data = await apiPost("/api/method/dynesourcerapi.application.add_to_applicant_record", payload);

    // Return the application_id to the caller for navigation
    return { success: true, data: { ...data, application_id: applicationId } };
  } catch (error) {
    console.error("addToApplicantRecord error:", error);
    return {
      success: false,
      error: error.message || "Failed to add product to applicant record"
    };
  }
};

/**
 * Add product to shortlist
 * 
 * @param {Object} productData - Product information
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const addShortlistedProduct = async (productData) => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");

    // Debug: Log incoming productData to trace fee type
    console.log("🔍 [addShortlistedProduct] Incoming productData fee fields:", {
      arrangement_fee_type: productData.arrangement_fee_type,
      product_fee_type: productData.product_fee_type,
      fee_type: productData.fee_type,
      arrangement_fee_percent: productData.arrangement_fee_percent,
      product_fee_percent: productData.product_fee_percent,
      product_fee_amount: productData.product_fee_amount,
      arrangement_fee: productData.arrangement_fee,
      arrangementFee: productData.arrangementFee,
    });

    // Backend requires 'mortgage_product' field (not 'product_id')
    const mortgageProduct = productData.mortgage_product ||
      productData.product_id ||
      productData.id ||
      productData.name;

    // Validate that mortgage_product is not empty
    if (!mortgageProduct || mortgageProduct === "" || mortgageProduct === "undefined" || mortgageProduct === null) {
      throw new Error(`mortgage_product is required but got: ${JSON.stringify(mortgageProduct)}. Product data: ${JSON.stringify(productData)}`);
    }

    // Ensure mortgage_product is a non-empty string
    const mortgageProductStr = String(mortgageProduct).trim();
    if (!mortgageProductStr || mortgageProductStr === "") {
      throw new Error(`mortgage_product cannot be empty after conversion. Original: ${JSON.stringify(mortgageProduct)}`);
    }

    // Validate mortgage_product before proceeding
    if (!mortgageProductStr || mortgageProductStr === "" || mortgageProductStr === null || mortgageProductStr === undefined) {
      const errorMsg = `CRITICAL: mortgage_product is missing or invalid! Value: ${JSON.stringify(mortgageProductStr)}`;
      console.error("❌", errorMsg);
      console.error("❌ Original productData:", productData);
      throw new Error(errorMsg);
    }

    // IMPORTANT: Backend structure:
    // - category/product_category: BTL, SC, AF (categories)
    // - product_type: Tracker, Fixed, Discounted, Variable (rate types)
    // Helper to determine if a value is a category (BTL, SC, AF)
    const isCategory = (val) => {
      if (!val) return false;
      const str = String(val).trim().toUpperCase();
      return ['BTL', 'CBTL', 'SC', 'AF', 'IF', 'ASSET', 'COMMERCIAL'].includes(str) ||
        str.includes('BUY-TO-LET') || str.includes('COMPLEX') || str.includes('INVOICE') || str.includes('ASSET FINANCE');
    };

    // Get category - from category fields (BTL, SC, AF)
    let productCategory = productData.product_category || productData.category;

    // If no category found, check if product_type/productType contains a category value
    if (!productCategory && productData.product_type && isCategory(productData.product_type)) {
      productCategory = productData.product_type;
    } else if (!productCategory && productData.productType && isCategory(productData.productType)) {
      productCategory = productData.productType;
    }

    // Default to BTL if no category specified
    if (!productCategory) {
      productCategory = 'BTL';
    }

    const isIfCategory = String(productCategory || "").trim().toUpperCase() === "IF";
    // IF products: term_months is often 0 on the Product doc; contract_term_months holds the real term.
    // Persist a positive value on both fields so get_my_shortlisted_products can round-trip it.
    const ifContractTermMonths = isIfCategory
      ? (() => {
          // Facility contract term only — never fall back to AB term (ab_term_months)
          const n = Number(
            productData.contract_term_months ??
              productData.termMonths ??
              productData.term_months ??
              productData._raw?.contract_term_months
          );
          return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
        })()
      : null;

    // Get rate type for product_type field (Tracker, Fixed, Discounted, Variable)
    // This should come from rate_type or product_type if it's a rate type
    let rateType = productData.rate_type || productData.rateType || null;

    // If rate_type is not set, check if product_type is actually a rate type
    if (!rateType && productData.product_type) {
      const pt = String(productData.product_type).trim();
      const ptUpper = pt.toUpperCase();
      // Check if it's a rate type (not a category)
      if (!isCategory(pt) && ['FIXED', 'VARIABLE', 'TRACKER', 'DISCOUNTED'].some(rt => ptUpper.includes(rt))) {
        rateType = pt; // Use product_type as rate type
      }
    }

    // Normalize rate type to match backend expectations (Tracker, Fixed, Discounted, Variable)
    if (rateType) {
      const rt = String(rateType).trim();
      const rtUpper = rt.toUpperCase();
      if (rtUpper.includes('TRACKER')) rateType = 'Tracker';
      else if (rtUpper.includes('FIXED')) rateType = 'Fixed';
      else if (rtUpper.includes('DISCOUNTED') || rtUpper.includes('DISCOUNT')) rateType = 'Discounted';
      else if (rtUpper.includes('VARIABLE')) rateType = 'Variable';
      else rateType = rt; // Keep original if not recognized
    }

    const orderedPayload = {
      mortgage_product: mortgageProductStr, // MUST be first - required by backend
      user: userEmail, // Required by backend
      interest_rate: productData.interest_rate || productData.rate || null, // Required by backend
      ltv: productData.ltv || null, // Required by backend

      // Additional fields that backend should receive
      product_id: mortgageProductStr,
      product_name: isIfCategory
        ? formatInvoiceFinanceProductTitle({
            ...productData,
            facility_type: resolveInvoiceFinanceFacilityType(productData),
            contract_term_months: ifContractTermMonths,
            termMonths: ifContractTermMonths,
          })
        : (productData.product_name || productData.name || null),
      lender_id: productData.lender_id || productData.lenderId || "unknown",
      lender_name: productData.lender_name || productData.lenderName || "Unknown Lender",
      // Backend Shortlisted Product: product_type is rate type only
      // (Fixed / Tracker / Variable / Discounted). IF vs BTL is category.
      product_type: rateType || "",
      category: productCategory, // BTL, SC, AF, IF
      product_category: productCategory, // Keep for compatibility
      rate_type: rateType || null, // Also send as rate_type for compatibility

      // Mortgage term - use mortgage_term_years from sourcing page, NOT product term
      // term_years should be the mortgage term (loan duration), not product term (rate period)
      term_years: productData.mortgage_term_years ?? null, // Use mortgage_term_years directly from sourcing
      term_months: isIfCategory
        ? (ifContractTermMonths ?? productData.term_months ?? productData.termMonths ?? null)
        : (productData.term_months ?? productData.termMonths ?? null),

      // Product term - the rate period (e.g., 0.5, 2, 5 years, Lifetime)
      ...(function () {
        const term = resolveProductTermFields(productData);
        if (!term.productTermRaw && term.productTermNumeric == null) return {};

        console.log("✅ Sending product term:", term, "for product:", productData.mortgage_product);

        return {
          // Backend select field: must be one of allowed numeric options or empty.
          product_termsyears: toBackendProductTermsYearsValue(
            term.productTermRaw,
            term.productTermNumeric
          ),
          product_term: term.productTermNumeric ?? term.productTermRaw,
          // Send numeric field only when numeric value exists.
          ...(term.productTermNumeric != null ? { product_term_years: term.productTermNumeric } : {}),
          // Preserve UI-readable term (e.g., Lifetime) in non-select fields.
          shortlisted_product_term: term.productTermRaw,
          shortlisted_product_term_years: term.productTermNumeric,
        };
      })(),
      // Prioritize explicit mortgage_term field from sourcing page
      // For IF, also store "N months" so list API round-trips contract term even if
      // contract_term_months is omitted from get_my_shortlisted_products.
      mortgage_term: isIfCategory && ifContractTermMonths
        ? `${ifContractTermMonths} months`
        : ((productData.mortgage_term && String(productData.mortgage_term).trim() !== "")
          ? productData.mortgage_term
          : (productData.mortgage_term_years ? `${productData.mortgage_term_years} yrs` : null)
          || null), // No default - allow null if not available

      loan_amount: productData.loan_amount || productData.mortgageAmount || productData.mortgage_amount || null,

      // Product fee / Arrangement fee — always store the value from sourcing (calculated £ amount or fee as sent)
      arrangement_fee: productData.arrangement_fee ?? productData.arrangementFee ?? null,
      product_fee: productData.arrangement_fee ?? productData.arrangementFee ?? productData.product_fee_amount ?? null,

      // product_fee_amount should be the percentage value (e.g., 4) when type is "Percentage"
      product_fee_amount: (() => {
        const rawType = productData.arrangement_fee_type || productData.product_fee_type || productData.fee_type || "";
        const typeLower = String(rawType).toLowerCase();
        const isPercentage = typeLower.includes("percent") || typeLower === "%";

        if (isPercentage) {
          const raw = productData.arrangement_fee_percent ?? productData.product_fee_percent ?? productData.fee_percent ??
            (parseFloat(productData.product_fee_amount) < 100 ? productData.product_fee_amount : null);
          return raw != null && raw !== "" ? Number(raw) : 0;
        } else {
          return productData.product_fee_amount ||
            productData.arrangement_fee ||
            productData.arrangementFee ||
            null;
        }
      })(),
      // Fee type - send both arrangement_fee_type and product_fee_type for backend compatibility
      // Backend uses product_fee_type as the primary field, so ensure it's set correctly.
      // 0% must be stored as Percentage with value 0, not Pound with 0.00.
      arrangement_fee_type: (() => {
        const rawType = productData.arrangement_fee_type || productData.product_fee_type || productData.fee_type || null;
        let type = rawType ? String(rawType).trim() : "";
        const typeLower = type.toLowerCase();

        const feeVal = parseFloat(productData.arrangement_fee || productData.product_fee_amount || 0);
        const percentVal = parseFloat(productData.arrangement_fee_percent ?? productData.product_fee_percent ?? productData.fee_percent ?? "");
        const hasZeroPercent = percentVal === 0 || productData.arrangement_fee_percent === 0 || productData.product_fee_amount === 0;

        // Explicit type wins: if source says Percentage, keep it (including 0%)
        if (typeLower.includes("percent") || typeLower === "%" || typeLower === "percentage") {
          return "Percentage";
        }
        // 0% case: type missing or empty but we have 0 percent/fee amount → treat as Percentage so backend stores 0%
        if ((type === "" || !rawType) && hasZeroPercent && feeVal === 0) {
          return "Percentage";
        }

        const looksLikePercent = (feeVal > 0 && feeVal < 100) || (percentVal > 0 && percentVal < 100);

        if (typeLower.includes("min") || typeLower.includes("minimum")) {
          return "Minimum arrangement fee";
        }
        if (typeLower.includes("pound") || typeLower.includes("£") || typeLower.includes("amount") || typeLower.includes("fixed") || typeLower === "gbp" || typeLower === "amount") {
          if (looksLikePercent && (type === "" || typeLower === "amount")) return "Percentage";
          return "Pound";
        }

        return type || (looksLikePercent || (hasZeroPercent && feeVal === 0) ? "Percentage" : "Pound");
      })(),
      product_fee_type: (() => {
        const rawType = productData.arrangement_fee_type || productData.product_fee_type || productData.fee_type || null;
        let type = rawType ? String(rawType).trim() : "";
        const typeLower = type.toLowerCase();

        const feeVal = parseFloat(productData.arrangement_fee || productData.product_fee_amount || 0);
        const percentVal = parseFloat(productData.arrangement_fee_percent ?? productData.product_fee_percent ?? productData.fee_percent ?? "");
        const hasZeroPercent = percentVal === 0 || productData.arrangement_fee_percent === 0 || productData.product_fee_amount === 0;

        if (typeLower.includes("percent") || typeLower === "%" || typeLower === "percentage") {
          return "Percentage";
        }
        if ((type === "" || !rawType) && hasZeroPercent && feeVal === 0) {
          return "Percentage";
        }

        const looksLikePercent = feeVal > 0 && feeVal < 100;

        if (typeLower.includes("min") || typeLower.includes("minimum")) {
          return "Minimum arrangement fee";
        }
        if (typeLower.includes("pound") || typeLower.includes("£") || typeLower.includes("amount") || typeLower.includes("fixed") || typeLower === "gbp") {
          if (looksLikePercent && (type === "" || typeLower === "amount")) return "Percentage";
          return "Pound";
        }
        return type || (looksLikePercent || (hasZeroPercent && feeVal === 0) ? "Percentage" : "Pound");
      })(),
      // Preserve 0 for 0% fee: when type is Percentage, send 0 not null
      arrangement_fee_percent: (() => {
        const raw = productData.arrangement_fee_percent ?? productData.product_fee_percent ?? productData.fee_percent;
        const rawType = productData.arrangement_fee_type || productData.product_fee_type || productData.fee_type || "";
        const isPct = String(rawType).toLowerCase().includes("percent");
        if (raw != null && raw !== "") return Number(raw);
        if (isPct) return 0; // type is Percentage but value missing → 0%
        return null;
      })(),
      min_arrangement_fee: productData.min_arrangement_fee || productData.min_fee || productData.product_min_fee || null,

      // Monthly payment - ensure we send even if 0
      // Use explicit checks to preserve 0 values (0 is a valid monthly payment)
      monthly_payment: (() => {
        const value = (productData.monthly_payment !== undefined && productData.monthly_payment !== null)
          ? productData.monthly_payment
          : (productData.monthlyCost !== undefined && productData.monthlyCost !== null)
            ? productData.monthlyCost
            : 0;

        // Debug logging
        console.log("🔍 [addShortlistedProduct] Monthly payment resolution:", {
          productId: mortgageProductStr,
          productData_monthly_payment: productData.monthly_payment,
          productData_monthlyCost: productData.monthlyCost,
          resolved_value: value,
          isZero: value === 0,
          isNull: value === null,
          isUndefined: value === undefined
        });

        return value;
      })(),

      // Total cost and fees
      total_cost: productData.total_cost || productData.totalCost || productData.cost_over_product_term || null,
      cost_over_product_term: productData.cost_over_product_term || productData.total_cost || productData.totalCost || null,
      total_fees: productData.total_fees || productData.totalFees ||
        (productData.arrangement_fee || productData.arrangementFee || productData.product_fee_amount || 0) || null,

      // APRC
      aprc: productData.aprc || productData.aprc_rate || productData.aprc_percent || null,

      // ICR rate
      icr_rate: productData.icr_rate || productData.icr || null,

      // Property value - required for mortgage illustration document
      // Use explicit check to preserve 0 values (0 is a valid property value)
      property_value: (() => {
        const pv = productData.property_value !== undefined && productData.property_value !== null
          ? productData.property_value
          : (productData.propertyValue !== undefined && productData.propertyValue !== null)
            ? productData.propertyValue
            : null;
        // Convert to number if it's a string, preserve null if not provided
        if (pv === null || pv === undefined) return null;
        const num = typeof pv === 'string' ? parseFloat(pv) : pv;
        return Number.isFinite(num) ? num : null;
      })(),

      // Deposit + estimated rent from BTL sourcing — needed for admin EOR filters
      deposit_amount: (() => {
        const raw =
          productData.deposit_amount ??
          productData.depositAmount ??
          null;
        if (raw === null || raw === undefined || raw === "") return 0.0;
        const num =
          typeof raw === "string"
            ? parseFloat(String(raw).replace(/[£,\s]/g, ""))
            : Number(raw);
        return Number.isFinite(num) ? num : 0.0;
      })(),
      est_rent: (() => {
        const raw =
          productData.est_rent ??
          productData.estimated_rent ??
          productData.estimatedRent ??
          productData.estRent ??
          null;
        if (raw === null || raw === undefined || raw === "") return 0.0;
        const num =
          typeof raw === "string"
            ? parseFloat(String(raw).replace(/[£,\s]/g, ""))
            : Number(raw);
        return Number.isFinite(num) ? num : 0.0;
      })(),
      // Alias — some backend paths / EOR use estimated_rent
      estimated_rent: (() => {
        const raw =
          productData.estimated_rent ??
          productData.est_rent ??
          productData.estimatedRent ??
          productData.estRent ??
          null;
        if (raw === null || raw === undefined || raw === "") return 0.0;
        const num =
          typeof raw === "string"
            ? parseFloat(String(raw).replace(/[£,\s]/g, ""))
            : Number(raw);
        return Number.isFinite(num) ? num : 0.0;
      })(),

      // Invoice Finance fields (Applications DocType)
      sector: productData.sector ?? null,
      projected_turnover: parseMoney(productData.projected_turnover ?? productData.projectedTurnover, 0),
      facility_required: parseMoney(productData.facility_required ?? productData.facilityRequired, 0),
      facility_type: isIfCategory
        ? resolveInvoiceFinanceFacilityType(productData)
        : (productData.facility_type ?? productData.facilityType ?? null),
      net_advance: parseMoney(productData.net_advance ?? productData.netAdvance, 0),
      additional_borrowing_amount: parseMoney(
        productData.additional_borrowing_amount ?? productData.additionalBorrowingAmount,
        0
      ),
      additional_borrowing:
        productData.additional_borrowing ??
        ((parseMoney(productData.additional_borrowing_amount ?? productData.additionalBorrowingAmount, 0) > 0) ? 1 : 0),
      confidential_facility: productData.confidential_facility ?? productData.confidentialFacility ?? 0,
      confidential: productData.confidential ?? 0,
      disclosure: productData.disclosure ?? 0,
      ip_percent: parseMoney(productData.ip_percent ?? productData.invoicePurchasePct, 0),
      discount_rate_percent: parseMoney(
        productData.discount_rate_percent ?? productData.discountRatePercent ?? productData.ratePct,
        0
      ),
      simple_service_fee: parseMoney(
        productData.service_fee_percent ??
          productData.simple_service_fee ??
          productData.annualCostPct,
        0
      ),
      service_fee_percent: parseMoney(
        productData.service_fee_percent ??
          productData.annualCostPct ??
          productData.simple_service_fee,
        0
      ),
      min_service_fee: parseMoney(
        productData.min_service_fee ?? productData.minServiceFee ?? productData.miniServiceFeePct,
        0
      ),
      apr: parseMoney(productData.apr ?? productData.ratePct ?? productData.rate, 0),
      // Prefer IF facility arrangement fee; keep existing arrangement_fee mapping above for BTL
      ...(productData.category === "IF" || productData.product_category === "IF" || productData.productType === "IF"
        ? {
            arrangement_fee: parseMoney(productData.arrangement_fee ?? productData.arrangementFee, 0),
            monthly_repayments: parseMoney(
              productData.monthly_repayments ?? productData.monthly_payment ?? productData.monthlyCost,
              0
            ),
            total_repayable: parseMoney(productData.total_repayable, 0),
            first_12_month_interest: parseMoney(productData.first_12_month_interest, 0),
            contract_term_months: parseMoney(
              ifContractTermMonths ??
                productData.contract_term_months ??
                productData.term_months ??
                productData.termMonths,
              0
            ),
            // AB arrangement fee % (Product.arrangement_fee_term) + computed £ — mirrors sourcing Full details
            arrangement_fee_term: parseMoney(
              productData.arrangement_fee_term ??
                productData.arrangementFeeTerm ??
                productData.abCalc?.arrangementFeePct,
              null
            ),
            ab_arrangement_fee: parseMoney(
              productData.ab_arrangement_fee ?? productData.abCalc?.arrangementFee,
              0
            ),
            ab_principal: parseMoney(
              productData.ab_principal ?? productData.abCalc?.abPrincipal,
              0
            ),
            ab_cost_over_product_term: parseMoney(
              productData.ab_cost_over_product_term ??
                productData.ab_cost_over_term ??
                productData.abCalc?.costOverTerm,
              0
            ),
            // Legacy alias kept for older consumers
            ab_cost_over_term: parseMoney(
              productData.ab_cost_over_product_term ??
                productData.ab_cost_over_term ??
                productData.abCalc?.costOverTerm,
              0
            ),
          }
        : {}),

      // Repayment type - required for mortgage illustration document
      repayment_method: productData.repayment_type || productData.repaymentType || "Repayment",
      repayment_type: productData.repayment_type || productData.repaymentType || "Repayment",

    };

    // Format ERC data if it exists and is an array
    // Backend expects field name: shortlisted_erc
    let formattedErc = null;
    const ercSource = productData.erc || productData.early_repayment_charges || productData.shortlisted_erc;
    if (ercSource && Array.isArray(ercSource) && ercSource.length > 0) {
      // Ensure ERC is in the format backend expects: [{ year_no: "Year 1", charge_percent: 7.0 }, ...]
      formattedErc = ercSource.map(erc => {
        if (typeof erc === "object" && erc !== null) {
          // Extract year number
          let yearNo = erc.year_no || erc.year || erc.yearNum;
          if (typeof yearNo === "string") {
            // Handle "Year 1" format
            yearNo = yearNo.replace("Year ", "").replace("year ", "").trim();
          }
          const yearNum = yearNo ? `Year ${yearNo}` : null;

          // Extract charge percent
          let chargePercent = erc.charge_percent || erc.charge;
          if (chargePercent == null && erc.val) {
            // Handle "7%" format
            chargePercent = parseFloat(String(erc.val).replace("%", "").trim());
          }

          return {
            year_no: yearNum || `Year ${erc.year || 1}`,
            charge_percent: chargePercent != null ? Number(chargePercent) : 0,
          };
        }
        return erc;
      }).filter(erc => erc && erc.year_no && erc.charge_percent != null);
    }

    // Add ERC fields - backend expects shortlisted_erc specifically
    if (formattedErc && formattedErc.length > 0) {
      orderedPayload.shortlisted_erc = formattedErc;
      orderedPayload.erc = formattedErc; // Also send as erc for compatibility
      orderedPayload.early_repayment_charges = formattedErc; // Also send as early_repayment_charges
    } else {
      // Send empty array if no ERC data (backend might expect this field)
      orderedPayload.shortlisted_erc = [];
    }

    // Remove null/undefined values to keep payload clean (but keep 0 values and empty arrays as they're valid)
    // IMPORTANT: Keep mortgage_term, monthly_payment, shortlisted_erc, icr_rate, deposit_amount, est_rent even if null/empty
    // Backend expects these fields
    const criticalFields = [
      'mortgage_term',
      'monthly_payment',
      'shortlisted_erc',
      'icr_rate',
      'deposit_amount',
      'est_rent',
      'estimated_rent',
      'sector',
      'projected_turnover',
      'facility_required',
      'facility_type',
      'net_advance',
      'additional_borrowing_amount',
      'additional_borrowing',
      'confidential_facility',
      'confidential',
      'disclosure',
      'ip_percent',
      'discount_rate_percent',
      'simple_service_fee',
      'service_fee_percent',
      'min_service_fee',
      'apr',
      'arrangement_fee',
      'arrangement_fee_term',
      'ab_arrangement_fee',
      'ab_principal',
      'ab_cost_over_product_term',
      'ab_cost_over_term',
      'contract_term_months',
      'category',
      'product_category',
      'product_type',
    ];
    Object.keys(orderedPayload).forEach(key => {
      // Don't remove critical fields even if null/empty - backend might need them
      if (criticalFields.includes(key)) {
        return; // Keep these fields
      }
      if (orderedPayload[key] === null || orderedPayload[key] === undefined) {
        delete orderedPayload[key];
      }
    });

    // Log the final payload to verify all fields
    console.log("📋 FINAL PAYLOAD WITH ALL FIELDS:", {
      mortgage_term: orderedPayload.mortgage_term,
      monthly_payment: orderedPayload.monthly_payment,
      shortlisted_erc: orderedPayload.shortlisted_erc,
      icr_rate: orderedPayload.icr_rate,
      arrangement_fee: orderedPayload.arrangement_fee,
      product_fee_amount: orderedPayload.product_fee_amount,
      arrangement_fee_type: orderedPayload.arrangement_fee_type,
      product_fee_type: orderedPayload.product_fee_type,
      arrangement_fee_percent: orderedPayload.arrangement_fee_percent,
      hasMortgageTerm: !!orderedPayload.mortgage_term,
      hasMonthlyPayment: orderedPayload.monthly_payment !== undefined && orderedPayload.monthly_payment !== null,
      hasErc: Array.isArray(orderedPayload.shortlisted_erc) && orderedPayload.shortlisted_erc.length > 0,
      hasIcr: orderedPayload.icr_rate !== undefined && orderedPayload.icr_rate !== null,
    });

    console.log("🚀 SENDING ORDERED PAYLOAD:", JSON.stringify(orderedPayload, null, 2));
    console.log("🚀 mortgage_product in ordered payload:", orderedPayload.mortgage_product);

    console.log("🚀 [addShortlistedProduct] SENDING ORDERED PAYLOAD:", JSON.stringify(orderedPayload, null, 2));
    console.log("🚀 [addShortlistedProduct] Key fields in payload:", {
      mortgage_product: orderedPayload.mortgage_product,
      monthly_payment: orderedPayload.monthly_payment,
      property_value: orderedPayload.property_value,
      deposit_amount: orderedPayload.deposit_amount,
      est_rent: orderedPayload.est_rent,
      estimated_rent: orderedPayload.estimated_rent,
      source_deposit_amount: productData.deposit_amount,
      source_est_rent: productData.est_rent,
      source_estimated_rent: productData.estimated_rent,
      category: orderedPayload.category,
      product_type: orderedPayload.product_type,
    });

    const data = await apiPost("/api/method/dynesourcerapi.shortlist_api.add_shortlisted_product", orderedPayload);

    console.log("📥 [addShortlistedProduct] Backend response:", {
      success: data?.message?.ok !== false,
      deposit_amount_sent: orderedPayload.deposit_amount,
      est_rent_sent: orderedPayload.est_rent,
      deposit_amount_in_response: data?.message?.deposit_amount ?? data?.message?.doc?.deposit_amount,
      est_rent_in_response: data?.message?.est_rent ?? data?.message?.doc?.est_rent,
      fullResponse: data
    });

    // Backend returns: { message: { ok: true, name: "...", status: "Pending", ... } }
    const result = data?.message || data;

    return {
      success: result?.ok !== false,
      data: result,
      duplicateBlocked: result?.duplicate_blocked || false
    };
  } catch (error) {
    console.error("addShortlistedProduct error:", error);
    return {
      success: false,
      error: error.message || "Failed to add product to shortlist"
    };
  }
};

/**
 * Remove product from shortlist by mortgage_product id (e.g. from sourcing page).
 * Fetches current shortlist, finds the shortlisted doc for this product, then deletes by doc name.
 * Use this when the backend only supports delete_shortlisted_product_sql (by doc name).
 *
 * @param {string} mortgageProductId - Product ID (mortgage_product)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const removeShortlistedProductByMortgageProduct = async (mortgageProductId) => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");
    if (!mortgageProductId) throw new Error("Product ID is required");

    const res = await getShortlistedProducts({ limit: 20 });
    const list = Array.isArray(res?.data) ? res.data : [];
    const idStr = String(mortgageProductId).trim();
    const item = list.find((p) => {
      const pid = (p.mortgage_product || p.product_id || p.id || "").toString().trim();
      const docName = (p.name || "").toString().trim();
      return (pid && pid === idStr) || (docName && docName === idStr);
    });
    if (!item) {
      return { success: false, error: "Product not found in shortlist" };
    }
    const docName = item.name;
    if (!docName) {
      return { success: false, error: "Shortlisted document name not found" };
    }
    return await deleteShortlistedProductByName(docName);
  } catch (error) {
    console.error("removeShortlistedProductByMortgageProduct error:", error);
    return {
      success: false,
      error: error.message || "Failed to remove product from shortlist"
    };
  }
};

/**
 * Delete shortlisted product by name (SQL API)
 * 
 * @param {string} productName - Product name (mortgage_product name)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteShortlistedProductByName = async (productName) => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");

    if (!productName) throw new Error("Product name is required");

    const payload = {
      user: userEmail,
      name: productName // Product name to delete
    };

    const data = await apiPost("/api/method/dynesourcerapi.shortlist_api.delete_shortlisted_product_sql", payload);
    const result = data?.message || data;

    return {
      success: result?.ok !== false
    };
  } catch (error) {
    console.error("deleteShortlistedProductByName error:", error);
    return {
      success: false,
      error: error.message || "Failed to delete shortlisted product"
    };
  }
};

/**
 * Delete all shortlisted products for the current user (SQL API)
 * 
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteAllShortlistedProducts = async () => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");

    const payload = {
      user: userEmail
    };

    const data = await apiPost("/api/method/dynesourcerapi.shortlist_api.delete_all_shortlisted_products_sql", payload);
    const result = data?.message || data;

    return {
      success: result?.ok !== false
    };
  } catch (error) {
    console.error("deleteAllShortlistedProducts error:", error);
    return {
      success: false,
      error: error.message || "Failed to delete all shortlisted products"
    };
  }
};

/**
 * Get user's shortlisted products from backend
 * 
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of products to fetch (default: 50)
 * @param {boolean} options.with_erc - Include Early Repayment Charges (default: false)
 * @param {string} options.status - Filter by status (e.g., "Pending")
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getShortlistedProducts = async (options = {}) => {
  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) throw new Error("User not authenticated");

    const { limit = 50, with_erc = false, status } = options;

    let url = `/api/method/dynesourcerapi.shortlist_api.get_my_shortlisted_products?limit=${limit}`;

    if (with_erc) {
      url += `&with_erc=1`;
    }

    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }

    // Add cache-busting parameter to prevent backend caching
    url += `&_t=${Date.now()}`;

    const data = await apiGet(url);

    // Backend returns: { message: { ok: true, items: [...] } }
    // Log the full response structure first
    console.log("📦 getShortlistedProducts RAW response:", {
      url,
      fullResponse: data,
      message: data?.message,
      messageOk: data?.message?.ok,
      messageItems: data?.message?.items,
      messageItemsType: typeof data?.message?.items,
      messageItemsIsArray: Array.isArray(data?.message?.items),
      messageItemsLength: data?.message?.items?.length,
      messageKeys: data?.message ? Object.keys(data.message) : [],
      // Also check if data itself is the array
      dataIsArray: Array.isArray(data),
      dataLength: Array.isArray(data) ? data.length : null,
    });

    // Try multiple paths to extract products array
    let products = [];

    // Path 1: data.message.items (most common)
    if (data?.message?.items && Array.isArray(data.message.items)) {
      products = data.message.items;
      console.log("✅ Found products in data.message.items");
    }
    // Path 2: data.message is the array directly
    else if (Array.isArray(data?.message)) {
      products = data.message;
      console.log("✅ Found products in data.message (array)");
    }
    // Path 3: data.data (alternative backend format)
    else if (Array.isArray(data?.data)) {
      products = data.data;
      console.log("✅ Found products in data.data");
    }
    // Path 4: data is the array directly
    else if (Array.isArray(data)) {
      products = data;
      console.log("✅ Found products in data (array)");
    }
    // Path 5: data.products
    else if (Array.isArray(data?.products)) {
      products = data.products;
      console.log("✅ Found products in data.products");
    }
    // Path 6: data.message.data
    else if (Array.isArray(data?.message?.data)) {
      products = data.message.data;
      console.log("✅ Found products in data.message.data");
    }
    else {
      console.warn("⚠️ Could not find products array in response");
      products = [];
    }

    // Log for debugging
    console.log("📦 getShortlistedProducts parsed products:", {
      itemsCount: Array.isArray(products) ? products.length : "NOT AN ARRAY",
      productsType: typeof products,
      productsIsArray: Array.isArray(products),
      hasItems: Array.isArray(products) && products.length > 0,
      firstItem: Array.isArray(products) && products.length > 0 ? products[0] : null,
      allProductIds: Array.isArray(products) ? products.map(p => p.mortgage_product || p.product_id || p.id || p.name) : []
    });

    // Ensure we always return an array
    const finalProducts = Array.isArray(products) ? products : [];

    return { success: true, data: finalProducts };
  } catch (error) {
    console.error("getShortlistedProducts error:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch shortlisted products",
      data: []
    };
  }
};

/**
 * Sync localStorage shortlist to backend after login
 * 
 * @returns {Promise<{success: boolean, synced: number, errors: Array}>}
 */
export const syncLocalStorageToBackend = async () => {
  const { STORAGE_KEYS } = require("../../utils/constants");
  const LS_PENDING = STORAGE_KEYS.SHORTLIST_PENDING;
  const LS_SHORTLIST = STORAGE_KEYS.SHORTLIST;

  try {
    const userEmail = getBorrowerEmail();
    if (!userEmail) {
      return { success: false, synced: 0, errors: ["User not authenticated"] };
    }

    // Read from localStorage
    const safeReadArray = (key) => {
      try {
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };

    const pending = safeReadArray(LS_PENDING);
    const confirmed = safeReadArray(LS_SHORTLIST);

    // IMPORTANT: Prioritize pending over confirmed
    // Pending has the full product data with monthly_payment from sourcing page
    // Confirmed might be from backend response or old format, missing monthly_payment
    // Merge strategy: Use pending first, then add confirmed products that aren't in pending
    const pendingIds = new Set(pending.map(p => p.id || p.product_id || p.mortgage_product));
    const confirmedNotInPending = confirmed.filter(p => {
      const id = p.id || p.product_id || p.mortgage_product;
      return !pendingIds.has(id);
    });
    const allProducts = [...pending, ...confirmedNotInPending].slice(0, 5); // Max 5

    // Debug: Check if products in confirmed are missing monthly_payment
    confirmed.forEach((p, idx) => {
      if (!p.monthly_payment && !p.monthlyCost && !p.repayment) {
        console.warn(`⚠️ [syncLocalStorageToBackend] Product ${p.id || p.product_id || p.mortgage_product} in confirmed is missing monthly_payment/monthlyCost/repayment. Full product:`, p);
      }
    });

    // Debug: Log what's in localStorage
    console.log("📦 [syncLocalStorageToBackend] Reading from localStorage:", {
      pendingCount: pending.length,
      confirmedCount: confirmed.length,
      totalProducts: allProducts.length,
      pendingProducts: pending.map(p => ({
        id: p.id || p.product_id || p.mortgage_product,
        monthly_payment: p.monthly_payment,
        monthlyCost: p.monthlyCost,
        property_value: p.property_value || p.propertyValue,
        allKeys: Object.keys(p),
        fullProduct: p // Show full product to see what's actually stored
      })),
      confirmedProducts: confirmed.map(p => ({
        id: p.id || p.product_id || p.mortgage_product,
        monthly_payment: p.monthly_payment,
        monthlyCost: p.monthlyCost,
        property_value: p.property_value || p.propertyValue,
        allKeys: Object.keys(p),
        fullProduct: p // Show full product to see what's actually stored
      }))
    });

    if (allProducts.length === 0) {
      console.log("ℹ️ [syncLocalStorageToBackend] No products in localStorage to sync");
      return { success: true, synced: 0, errors: [], shortlistFull: false };
    }

    // Fetch current backend shortlist: enforce max 5 and avoid duplicates
    const backendRes = await getShortlistedProducts({ limit: 10 });
    const backendProducts = Array.isArray(backendRes?.data) ? backendRes.data : [];
    const MAX_SHORTLIST = 5;
    if (backendProducts.length >= MAX_SHORTLIST) {
      console.log("⚠️ [syncLocalStorageToBackend] Backend shortlist is full (5 products). Not syncing.");
      return {
        success: false,
        synced: 0,
        errors: ["Shortlist is full (5 products). Remove one from your shortlist to add more."],
        shortlistFull: true
      };
    }

    const existingIds = new Set(
      backendProducts.map(p => (p.mortgage_product || p.product_id || p.id || p.name || "").toString().trim()).filter(Boolean)
    );
    const toSync = allProducts.filter(p => {
      const id = (p.id || p.product_id || p.mortgage_product || p.name || "").toString().trim();
      return id && !existingIds.has(id);
    });
    const maxToAdd = MAX_SHORTLIST - backendProducts.length;
    const toSyncCapped = toSync.slice(0, maxToAdd);

    const errors = [];
    let synced = 0;

    // POST each product to backend (no duplicates, never exceed 5)
    for (const product of toSyncCapped) {
      try {
        // Backend requires 'mortgage_product' field
        const mortgageProduct = product.mortgage_product ||
          product.product_id ||
          product.id ||
          product.name;

        // Validate mortgage_product before proceeding
        if (!mortgageProduct || mortgageProduct === "" || mortgageProduct === "undefined" || mortgageProduct === null) {
          console.error("❌ Invalid mortgage_product for product:", {
            product,
            mortgage_product: product.mortgage_product,
            product_id: product.product_id,
            id: product.id,
            name: product.name
          });
          errors.push(`Invalid mortgage_product for product: ${JSON.stringify({ id: product.id, name: product.name })}`);
          continue;
        }

        // Get mortgage term - use mortgage_term_years from sourcing page, NOT product term
        // mortgage_term_years is the loan duration (e.g., 25 years), NOT product term (rate period)
        const mortgageTermYears = product.mortgage_term_years ?? null; // Use mortgage_term_years directly
        let termMonths = product.termMonths ?? product.term_months;
        // Prioritize explicit mortgage_term field from sourcing page
        let mortgageTermStr = (product.mortgage_term && String(product.mortgage_term).trim() !== "")
          ? product.mortgage_term
          : (mortgageTermYears ? `${mortgageTermYears} yrs` : null);

        // Get ERC data if available from product or _raw
        let ercData = null;
        const rawProduct = product._raw || product;
        if (product.erc || product.early_repayment_charges || product.shortlisted_erc) {
          const ercSource = product.erc || product.early_repayment_charges || product.shortlisted_erc;
          if (Array.isArray(ercSource) && ercSource.length > 0) {
            ercData = ercSource;
          }
        } else if (rawProduct) {
          // Try to extract ERC from raw product (year_1, year_2, etc.)
          const ercYears = [];
          for (let y = 1; y <= 5; y++) {
            const yearKey = `year_${y}`;
            const charge = rawProduct[yearKey] || rawProduct[`erc_year_${y}`] || rawProduct[`early_repayment_year_${y}`];
            if (charge != null && Number.isFinite(Number(charge)) && Number(charge) > 0) {
              ercYears.push({
                year_no: `Year ${y}`,
                charge_percent: Number(charge),
                year: y,
                charge: Number(charge),
              });
            }
          }
          if (ercYears.length > 0) {
            ercData = ercYears;
          }
        }

        // IMPORTANT: Backend structure:
        // - category/product_category: BTL, SC, AF (categories)
        // - product_type: Tracker, Fixed, Discounted, Variable (rate types)
        // Helper to determine if a value is a category (BTL, SC, AF)
        const isCategory = (val) => {
          if (!val) return false;
          const str = String(val).trim().toUpperCase();
          return ['BTL', 'CBTL', 'SC', 'AF', 'IF', 'ASSET', 'COMMERCIAL'].includes(str) ||
            str.includes('BUY-TO-LET') || str.includes('COMPLEX') || str.includes('INVOICE') || str.includes('ASSET FINANCE');
        };

        // Get category - from category fields (BTL, SC, AF)
        let productCategory = product.product_category || product.category;

        // If no category found, check if product_type/productType contains a category value
        if (!productCategory && product.product_type && isCategory(product.product_type)) {
          productCategory = product.product_type;
        } else if (!productCategory && product.productType && isCategory(product.productType)) {
          productCategory = product.productType;
        }

        // Default to BTL if no category specified
        if (!productCategory) {
          productCategory = 'BTL';
        }

        const isIfCategory = String(productCategory || "").trim().toUpperCase() === "IF";
        if (isIfCategory) {
          const ifTerm = Number(
            product.contract_term_months ?? product.termMonths ?? product.term_months
          );
          if (Number.isFinite(ifTerm) && ifTerm > 0) {
            termMonths = Math.round(ifTerm);
            mortgageTermStr = `${termMonths} months`;
          }
        }

        // Get rate type for product_type field (Tracker, Fixed, Discounted, Variable)
        let rateType = product.rate_type || product.rateType || null;

        // If rate_type is not set, check if product_type is actually a rate type
        if (!rateType && product.product_type) {
          const pt = String(product.product_type).trim();
          const ptUpper = pt.toUpperCase();
          // Check if it's a rate type (not a category)
          if (!isCategory(pt) && ['FIXED', 'VARIABLE', 'TRACKER', 'DISCOUNTED'].some(rt => ptUpper.includes(rt))) {
            rateType = pt; // Use product_type as rate type
          }
        }

        // Normalize rate type to match backend expectations (Tracker, Fixed, Discounted, Variable)
        if (rateType) {
          const rt = String(rateType).trim();
          const rtUpper = rt.toUpperCase();
          if (rtUpper.includes('TRACKER')) rateType = 'Tracker';
          else if (rtUpper.includes('FIXED')) rateType = 'Fixed';
          else if (rtUpper.includes('DISCOUNTED') || rtUpper.includes('DISCOUNT')) rateType = 'Discounted';
          else if (rtUpper.includes('VARIABLE')) rateType = 'Variable';
          else rateType = rt; // Keep original if not recognized
        }

        // Resolve monthly payment with better fallbacks
        // Use explicit checks to preserve 0 values (0 is a valid monthly payment)
        // Check multiple possible field names and also check _raw for the original value
        // Note: rawProduct is already declared above for ERC data

        // Debug: Log all monthly payment sources
        console.log("🔍 [syncLocalStorageToBackend] Monthly payment resolution for product:", {
          productId: mortgageProduct,
          product_monthly_payment: product.monthly_payment,
          product_monthlyCost: product.monthlyCost,
          product_repayment: product.repayment, // Check for repayment field (backend format)
          raw_monthly_payment: rawProduct?.monthly_payment,
          raw_monthlyCost: rawProduct?.monthlyCost,
          raw_repayment: rawProduct?.repayment, // Check for repayment in _raw
          hasRaw: !!rawProduct,
          rawKeys: rawProduct ? Object.keys(rawProduct).filter(k => k.toLowerCase().includes('monthly') || k.toLowerCase().includes('payment') || k.toLowerCase().includes('repayment')) : [],
          allProductKeys: Object.keys(product),
          allRawKeys: rawProduct ? Object.keys(rawProduct) : []
        });

        // Check for monthly payment in various field names (including 'repayment' from backend)
        const monthlyPayment = (product.monthly_payment !== undefined && product.monthly_payment !== null && product.monthly_payment > 0)
          ? product.monthly_payment
          : (product.monthlyCost !== undefined && product.monthlyCost !== null && product.monthlyCost > 0)
            ? product.monthlyCost
            : (product.repayment !== undefined && product.repayment !== null && product.repayment > 0) // Backend might use 'repayment'
              ? product.repayment
              : (rawProduct?.monthly_payment !== undefined && rawProduct?.monthly_payment !== null && rawProduct.monthly_payment > 0)
                ? rawProduct.monthly_payment
                : (rawProduct?.monthlyCost !== undefined && rawProduct?.monthlyCost !== null && rawProduct.monthlyCost > 0)
                  ? rawProduct.monthlyCost
                  : (rawProduct?.repayment !== undefined && rawProduct?.repayment !== null && rawProduct.repayment > 0) // Check repayment in _raw
                    ? rawProduct.repayment
                    : (product.monthly_payment !== undefined && product.monthly_payment !== null)
                      ? product.monthly_payment
                      : (product.monthlyCost !== undefined && product.monthlyCost !== null)
                        ? product.monthlyCost
                        : (product.repayment !== undefined && product.repayment !== null) // Accept 0 values from repayment
                          ? product.repayment
                          : 0;

        console.log("✅ [syncLocalStorageToBackend] Resolved monthly payment:", monthlyPayment);

        const resolvedProductTerm = resolveProductTermFields(product);

        const sourcingSnapshot = readSourcingSnapshot() || {};
        const resolvedPropertyValue = (() => {
          const pv = product.property_value !== undefined && product.property_value !== null
            ? product.property_value
            : (product.propertyValue !== undefined && product.propertyValue !== null)
              ? product.propertyValue
              : null;
          if (pv === null || pv === undefined) return null;
          const num = typeof pv === 'string' ? parseFloat(pv) : pv;
          return Number.isFinite(num) ? num : null;
        })();
        const resolvedLoanAmount =
          parseMoney(product.loan_amount, 0) ||
          parseMoney(product.mortgageAmount, 0) ||
          parseMoney(product.mortgage_amount, 0);
        const resolvedDepositAmount = (() => {
          const explicit = parseMoney(product.deposit_amount ?? product.depositAmount, 0);
          if (explicit > 0) return explicit;
          const fromSnapshot = parseMoney(sourcingSnapshot?.deposit_amount, 0);
          if (fromSnapshot > 0) return fromSnapshot;
          if (resolvedPropertyValue > 0 && resolvedLoanAmount > 0) {
            return Math.max(0, resolvedPropertyValue - resolvedLoanAmount);
          }
          return 0.0;
        })();
        const resolvedEstRent = (() => {
          const explicit = parseMoney(
            product.est_rent ??
            product.estimated_rent ??
            product.estimatedRent ??
            product.estRent,
            0
          );
          if (explicit > 0) return explicit;
          const fromSnapshot = parseMoney(sourcingSnapshot?.est_rent, 0);
          return fromSnapshot > 0 ? fromSnapshot : 0.0;
        })();

        const productData = {
          mortgage_product: String(mortgageProduct).trim(),
          product_id: String(mortgageProduct).trim(),
          product_name: product.name || product.product_name,
          lender_id: product.lender_id || product.lenderId || "unknown",
          lender_name: product.lender_name || product.lenderName || "Unknown Lender",
          interest_rate: product.rate || product.interest_rate,
          // Shortlisted Product: product_type is rate type only. IF vs BTL is category.
          product_type: rateType || "",
          category: productCategory, // BTL, SC, AF, IF
          product_category: productCategory, // Keep for compatibility
          rate_type: rateType || null, // Also send as rate_type for compatibility
          term_years: mortgageTermYears, // Use mortgage_term_years (loan duration), NOT product term
          term_months: termMonths,
          mortgage_term: mortgageTermStr,
          // Product term - preserve raw text like "Lifetime"; send numeric companion only when available
          product_term: resolvedProductTerm.productTermNumeric,
          product_termsyears: toBackendProductTermsYearsValue(
            resolvedProductTerm.productTermRaw,
            resolvedProductTerm.productTermNumeric
          ),
          ...(resolvedProductTerm.productTermNumeric == null && resolvedProductTerm.productTermRaw
            ? { product_term: resolvedProductTerm.productTermRaw }
            : {}),
          product_term_years: resolvedProductTerm.productTermNumeric,
          shortlisted_product_term: resolvedProductTerm.productTermRaw,
          shortlisted_product_term_years: resolvedProductTerm.productTermNumeric,
          product_terms_years: resolvedProductTerm.productTermNumeric,
          ltv: product.ltv,
          loan_amount: product.loan_amount || product.mortgageAmount || product.mortgage_amount || 0,
          arrangement_fee: product.arrangement_fee ?? product.arrangementFee ?? product.product_fee_amount ?? 0,
          product_fee: product.arrangement_fee ?? product.arrangementFee ?? product.product_fee_amount ?? 0,
          product_fee_amount: (() => {
            const feeType = (product.arrangement_fee_type ?? product.product_fee_type ?? product.fee_type ?? "").toString().toLowerCase();
            const isPct = feeType.includes("percent");
            if (isPct) return product.arrangement_fee_percent ?? product.product_fee_amount ?? product.product_fee_percent ?? product.fee_percent ?? 0;
            return product.product_fee_amount ?? product.arrangement_fee ?? product.arrangementFee ?? 0;
          })(),
          arrangement_fee_type: product.arrangement_fee_type ?? product.product_fee_type ?? product.fee_type,
          product_fee_type: product.product_fee_type ?? product.arrangement_fee_type ?? product.fee_type,
          arrangement_fee_percent: (() => {
            const raw = product.arrangement_fee_percent ?? product.product_fee_percent ?? product.fee_percent;
            const feeType = (product.arrangement_fee_type ?? product.product_fee_type ?? product.fee_type ?? "").toString().toLowerCase();
            const isPct = feeType.includes("percent");
            if (raw != null && raw !== "") return Number(raw);
            if (isPct) return 0;
            return null;
          })(),
          min_arrangement_fee: product.min_arrangement_fee ?? product.min_fee ?? product.product_min_fee,
          monthly_payment: monthlyPayment,
          total_cost: product.totalCost || product.total_cost || product.cost_over_product_term || 0,
          cost_over_product_term: product.cost_over_product_term || product.totalCost || product.total_cost || 0,
          total_fees: product.total_fees || product.totalFees || (product.arrangementFee || product.arrangement_fee || product.product_fee_amount || 0),
          // ICR from backend only - do NOT calculate as 1% of loan amount
          icr_rate: product.icr_rate || product.icr || null,
          aprc: product.aprc || product.aprc_rate || product.aprc_percent || rawProduct?.aprc_percent || "",
          erc: ercData,
          early_repayment_charges: ercData,
          shortlisted_erc: ercData,
          // Property value - required for mortgage illustration document
          // Use explicit check to preserve 0 values (0 is a valid property value)
          property_value: resolvedPropertyValue,
          deposit_amount: resolvedDepositAmount,
          est_rent: resolvedEstRent,
          estimated_rent: resolvedEstRent,
          // Repayment type - required for mortgage illustration document (send both variations)
          repayment_type: product.repayment_type || product.repaymentType || "Repayment",
          repaymentType: product.repayment_type || product.repaymentType || "Repayment",
          repayment_method: product.repayment_type || product.repaymentType || "Repayment",

          // Invoice Finance fields (preserved from IF sourcing pending shortlist)
          sector: product.sector ?? null,
          projected_turnover: product.projected_turnover ?? product.projectedTurnover ?? 0,
          facility_required: product.facility_required ?? product.facilityRequired ?? 0,
          facility_type: (() => {
            const cat = String(productCategory || "").toUpperCase();
            if (cat === "IF") {
              const existing = String(product.facility_type ?? product.facilityType ?? "").trim();
              if (/^confidential$/i.test(existing)) return "Confidential";
              if (/^disclosure$|^disclosed$/i.test(existing)) return "Disclosure";
              const conf =
                product.confidential_facility ??
                product.confidentialFacility ??
                product.confidential ??
                0;
              return conf === true || conf === 1 || conf === "1" ? "Confidential" : "Disclosure";
            }
            return product.facility_type ?? product.facilityType ?? null;
          })(),
          net_advance: product.net_advance ?? product.netAdvance ?? 0,
          additional_borrowing_amount:
            product.additional_borrowing_amount ?? product.additionalBorrowingAmount ?? 0,
          additional_borrowing: product.additional_borrowing ?? 0,
          confidential_facility: product.confidential_facility ?? product.confidentialFacility ?? 0,
          confidential: product.confidential ?? 0,
          disclosure: product.disclosure ?? 0,
          ip_percent: product.ip_percent ?? product.invoicePurchasePct ?? 0,
          discount_rate_percent: product.discount_rate_percent ?? product.discountRatePercent ?? 0,
          simple_service_fee: product.simple_service_fee ?? product.annualCostPct ?? 0,
          min_service_fee: product.min_service_fee ?? product.minServiceFee ?? 0,
          apr: product.apr ?? product.ratePct ?? product.rate ?? 0,
          monthly_repayments: product.monthly_repayments ?? monthlyPayment ?? 0,
          total_repayable: product.total_repayable ?? 0,
          first_12_month_interest: product.first_12_month_interest ?? 0,
          contract_term_months: product.contract_term_months ?? termMonths ?? 0,
          arrangement_fee_term:
            product.arrangement_fee_term ??
            product.arrangementFeeTerm ??
            product.abCalc?.arrangementFeePct ??
            null,
          ab_arrangement_fee:
            product.ab_arrangement_fee ?? product.abCalc?.arrangementFee ?? 0,
          ab_principal: product.ab_principal ?? product.abCalc?.abPrincipal ?? 0,
          ab_cost_over_product_term:
            product.ab_cost_over_product_term ??
            product.ab_cost_over_term ??
            product.abCalc?.costOverTerm ??
            0,
          ab_cost_over_term:
            product.ab_cost_over_product_term ??
            product.ab_cost_over_term ??
            product.abCalc?.costOverTerm ??
            0,
        };

        console.log("🔄 [syncLocalStorageToBackend] Syncing product from localStorage:", {
          mortgage_product: productData.mortgage_product,
          product_id: productData.product_id,
          name: productData.product_name,
          mortgage_term: productData.mortgage_term,
          mortgage_term_years: mortgageTermYears,
          monthly_payment: productData.monthly_payment,
          source_monthly_payment: product.monthly_payment,
          source_monthlyCost: product.monthlyCost,
          property_value: productData.property_value,
          property_value_type: typeof productData.property_value,
          property_value_isZero: productData.property_value === 0,
          property_value_isNull: productData.property_value === null,
          property_value_isUndefined: productData.property_value === undefined,
          source_property_value: product.property_value,
          source_propertyValue: product.propertyValue,
          source_deposit_amount: product.deposit_amount,
          source_est_rent: product.est_rent,
          resolved_deposit_amount: productData.deposit_amount,
          resolved_est_rent: productData.est_rent,
          hasPropertyValue: 'property_value' in product,
          hasPropertyValueAlt: 'propertyValue' in product,
          icr_rate: productData.icr_rate,
          source_icr_rate: product.icr_rate,
          source_icr: product.icr,
          fullProductData: productData, // Full payload being sent
        });

        console.log("🚀 [syncLocalStorageToBackend] Calling addShortlistedProduct with payload:", {
          mortgage_product: productData.mortgage_product,
          monthly_payment: productData.monthly_payment,
          property_value: productData.property_value,
          payloadKeys: Object.keys(productData),
          fullPayload: productData
        });

        const result = await addShortlistedProduct(productData);
        if (result.success) {
          synced++;
          existingIds.add(String(mortgageProduct).trim());
          console.log("✅ [syncLocalStorageToBackend] Successfully synced product:", {
            mortgage_product: productData.mortgage_product,
            monthly_payment: productData.monthly_payment,
            property_value: productData.property_value,
            result: result.data
          });
        } else {
          errors.push(`Failed to sync product ${mortgageProduct}: ${result.error}`);
          console.error("❌ [syncLocalStorageToBackend] Failed to sync product:", {
            mortgage_product: productData.mortgage_product,
            monthly_payment: productData.monthly_payment,
            property_value: productData.property_value,
            error: result.error
          });
        }
      } catch (err) {
        errors.push(`Error syncing product: ${err.message}`);
        console.error("❌ Exception syncing product:", err);
      }
    }

    // Clear localStorage after successful sync
    if (synced > 0) {
      try {
        localStorage.removeItem(LS_PENDING);
        localStorage.removeItem(LS_SHORTLIST);
      } catch (e) {
        console.warn("Failed to clear localStorage after sync:", e);
      }
    }

    return { success: errors.length === 0, synced, errors, shortlistFull: false };
  } catch (error) {
    console.error("syncLocalStorageToBackend error:", error);
    return {
      success: false,
      synced: 0,
      errors: [error.message || "Failed to sync shortlist to backend"],
      shortlistFull: false
    };
  }
};